import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import ArchiveCard, { ArchiveItem } from "@/components/archive/ArchiveCard";
import { Input } from "@/components/ui/input";
import { Search, Library } from "lucide-react";
import { useCuratedPosts } from "@/hooks/useCuratedPosts";

const CATEGORIES = ["All", "Policy", "Community", "Technology", "Environment", "Education", "Housing", "Health", "Economic"];

type SourceFilter = "all" | "community" | "curated";
type TypeFilter = "all" | "concept" | "project";
type SortFilter = "newest" | "top";

const Archive = () => {
  const [source, setSource] = useState<SourceFilter>("all");
  const [typeF, setTypeF] = useState<TypeFilter>("all");
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState<SortFilter>("newest");
  const [q, setQ] = useState("");

  const { data: ideas = [] } = useQuery({
    queryKey: ["archive-ideas"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ideas")
        .select("id,title,description,image_url,category,idea_type,vote_count,created_at,status")
        .eq("status", "published")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: curated = [] } = useCuratedPosts();

  const items = useMemo<ArchiveItem[]>(() => {
    const ideaItems: ArchiveItem[] = ideas.map((i: any) => ({
      kind: "idea",
      id: i.id,
      title: i.title,
      summary: i.description,
      cover: i.image_url,
      category: i.category,
      type: i.idea_type ?? "concept",
      date: i.created_at,
      vote_count: i.vote_count ?? 0,
      href: `/ideas/${i.id}`,
    }));
    const curItems: ArchiveItem[] = curated.map((c) => ({
      kind: "curated",
      id: c.id,
      title: c.title,
      summary: c.summary,
      cover: c.cover_image_url,
      category: c.category,
      source_org: c.source_org_name,
      source_url: c.source_url,
      date: c.published_at ?? c.created_at,
      href: `/archive/curated/${c.slug}`,
    }));

    let merged = [...ideaItems, ...curItems];

    if (source === "community") merged = merged.filter((m) => m.kind === "idea");
    if (source === "curated") merged = merged.filter((m) => m.kind === "curated");
    if (typeF !== "all")
      merged = merged.filter((m) => m.kind === "idea" && m.type === typeF);
    if (category !== "All")
      merged = merged.filter((m) => (m.category ?? "").toLowerCase() === category.toLowerCase());
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      merged = merged.filter(
        (m) =>
          m.title.toLowerCase().includes(needle) ||
          (m.summary ?? "").toLowerCase().includes(needle),
      );
    }
    if (sort === "top") {
      merged.sort((a, b) => {
        const av = a.kind === "idea" ? a.vote_count : 0;
        const bv = b.kind === "idea" ? b.vote_count : 0;
        return bv - av;
      });
    } else {
      merged.sort((a, b) => +new Date(b.date) - +new Date(a.date));
    }
    return merged;
  }, [ideas, curated, source, typeF, category, sort, q]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="mb-8">
            <div className="flex items-center gap-3">
              <Library className="h-8 w-8 text-primary" />
              <h1 className="font-display text-3xl font-bold">Archive</h1>
            </div>
            <p className="text-muted-foreground mt-1 max-w-2xl">
              A browseable archive of community ideas, ongoing projects, and curated work from
              organizations whose work inspires us. Every external entry links back to the original
              source.
            </p>
          </div>

          {/* Filter bar */}
          <div className="sticky top-16 z-30 bg-background/85 backdrop-blur-xl border-b border-border py-3 mb-6 -mx-4 sm:-mx-6 px-4 sm:px-6 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {(["all", "community", "curated"] as SourceFilter[]).map((s) => (
                <button
                  key={s}
                  onClick={() => setSource(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
                    source === s
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  }`}
                >
                  {s === "all" ? "All sources" : s}
                </button>
              ))}
              <span className="w-px h-5 bg-border mx-1" />
              {source !== "curated" &&
                (["all", "concept", "project"] as TypeFilter[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTypeF(t)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
                      typeF === t
                        ? "bg-primary/15 text-primary border border-primary/30"
                        : "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-transparent"
                    }`}
                  >
                    {t === "all" ? "All types" : `${t}s`}
                  </button>
                ))}
              <span className="w-px h-5 bg-border mx-1" />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortFilter)}
                className="bg-secondary border border-border rounded-full px-3 py-1.5 text-xs"
              >
                <option value="newest">Newest</option>
                <option value="top">Most upvoted</option>
              </select>
              <div className="relative ml-auto w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search archive…"
                  className="pl-8 h-8 rounded-full text-xs"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                    category === c
                      ? "bg-foreground text-background"
                      : "bg-secondary/60 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <p className="text-lg mb-1">Nothing matches those filters.</p>
              <p className="text-sm">Try a different source or category.</p>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
              {items.map((it) => (
                <ArchiveCard key={`${it.kind}-${it.id}`} item={it} />
              ))}
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Archive;